import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AuthLayout, FormError } from "@/components/auth/AuthLayout";
import { CaptchaField } from "@/components/auth/CaptchaField";
import { GoogleButton, OrDivider } from "@/components/auth/GoogleButton";
import { PasswordInput } from "@/components/auth/PasswordInput";
import { FormField, inputClass } from "@/components/form/FormField";
import { useAuth } from "@/context/AuthContext";
import { useCaptcha } from "@/hooks/useCaptcha";
import { formatApiError } from "@/lib/api";

const EMPTY = { name: "", username: "", email: "", phone: "", password: "", confirm: "" };
const FIELDS = [
  { key: "name", label: "Nama Lengkap", placeholder: "Nama sesuai KTP", autoComplete: "name" },
  { key: "username", label: "Username", placeholder: "contoh: budi.merak", autoComplete: "username", hint: "3-30 karakter: huruf kecil, angka, titik, garis bawah" },
  { key: "email", label: "Email", placeholder: "nama@email.com", autoComplete: "email", type: "email" },
  { key: "phone", label: "Nomor HP (WhatsApp)", placeholder: "081234567890", autoComplete: "tel", type: "tel" },
];

const validate = (f) => {
  const e = {};
  FIELDS.forEach(({ key, label }) => { if (!f[key].trim()) e[key] = `${label} wajib diisi`; });
  if (!e.username && !/^[a-z0-9._]{3,30}$/.test(f.username.trim().toLowerCase())) e.username = "Username tidak valid";
  if (!e.email && !/^\S+@\S+\.\S+$/.test(f.email.trim())) e.email = "Format email tidak valid";
  if (!e.phone && !/^(\+?62|0)8\d{7,12}$/.test(f.phone.replace(/[\s-]/g, ""))) e.phone = "Format nomor HP tidak valid";
  if (f.password.length < 8) e.password = "Password minimal 8 karakter";
  if (f.confirm !== f.password) e.confirm = "Konfirmasi password tidak sama";
  return e;
};

export default function RegisterPage() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const captcha = useCaptcha();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/"} replace />;
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length) return;
    if (!captcha.answer.trim()) return setError("Masukkan kode captcha.");
    setError("");
    setLoading(true);
    try {
      const { confirm, ...body } = form;
      const u = await register({ ...body, username: body.username.trim().toLowerCase(), ...captcha.payload });
      toast.success(`Akun berhasil dibuat. Selamat datang, ${u.name}!`);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err, "Pendaftaran gagal. Silakan coba lagi."));
      captcha.refresh();
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Daftar Akun Warga" description="Buat akun untuk mengirim dan memantau pengaduan Anda." testId="register-page">
      <form onSubmit={submit} className="space-y-4" noValidate data-testid="register-form">
        <div className="grid gap-4 sm:grid-cols-2">
          {FIELDS.map(({ key, label, placeholder, autoComplete, type, hint }) => (
            <FormField key={key} id={`reg-${key}`} label={label} required error={errors[key]} hint={hint}>
              <Input id={`reg-${key}`} type={type || "text"} value={form[key]} onChange={(e) => set(key)(e.target.value)} placeholder={placeholder} autoComplete={autoComplete} className={inputClass(errors[key])} data-testid={`register-${key}-input`} />
            </FormField>
          ))}
          <FormField id="reg-password" label="Password" required error={errors.password}>
            <PasswordInput id="reg-password" value={form.password} onChange={set("password")} placeholder="Minimal 8 karakter" autoComplete="new-password" testId="register-password-input" />
          </FormField>
          <FormField id="reg-confirm" label="Ulangi Password" required error={errors.confirm}>
            <PasswordInput id="reg-confirm" value={form.confirm} onChange={set("confirm")} placeholder="Ketik ulang password" autoComplete="new-password" testId="register-confirm-input" />
          </FormField>
        </div>
        <CaptchaField captcha={captcha} />
        <FormError message={error} testId="register-error" />
        <Button type="submit" disabled={loading} className="h-11 w-full rounded-xl bg-emerald-600 font-semibold hover:bg-emerald-700" data-testid="register-submit-btn">
          {loading && <LoaderCircle className="h-4 w-4 animate-spin" />} Daftar
        </Button>
      </form>
      <OrDivider />
      <GoogleButton label="Daftar dengan Google" />
      <p className="text-center text-sm text-slate-500">
        Sudah punya akun? <Link to="/masuk" className="font-semibold text-emerald-700 hover:underline" data-testid="go-login-link">Masuk</Link>
      </p>
    </AuthLayout>
  );
}
